import React from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { StatusCodes } from 'http-status-codes';
import { useFormik } from 'formik';
import { PopupPacket } from './DropabbleColumn';
import LocationEditModal from '~/src/components/common/LocationEditModal';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import { statusSchema } from '~/src/scenes/warehouses/inventory/InventorySchema';
import InventoryApi from '~/src/services/api/InventoryApi';
import xCircle from '../../../../../../static/img/xCircle.svg';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import { quantityGetDtoToStockTransactionAddDto } from '~/src/types/convertors/quantityGetDtoToStockTransactionAddDto';
import { StockTransactionAddDto } from '~/src/types/StockTransactionAddDto';
import { ChangeStockTransactionDto } from '~/src/types/ChangeStockTransactionDto';
import { quantityGetDtoToFormik } from '~/src/types/convertors/quantityGetDtoToFormik';

interface FormikTypes {
  singleCuUnits: string;
  singleDpUnits: string;
  fullDuUnits: string;
  column: string;
  letter: string;
  chamberNumber: string;
  level: string;
}

interface Types {
  initialData: PopupPacket;
}

export default function StatusModal({ initialData }: Types): JSX.Element {
  const fullFormikObject = quantityGetDtoToFormik(initialData.item, true);
  const { singleCuUnits, singleDpUnits, fullDuUnits, chamberNumber, column, letter, level } = fullFormikObject;
  const initialValues: FormikTypes = {
    singleCuUnits,
    singleDpUnits,
    fullDuUnits,
    chamberNumber,
    column,
    letter,
    level,
  };
  const formik = useFormik({
    initialValues,
    validationSchema: statusSchema,
    onSubmit: async (values, { setSubmitting }) => {
      return saveItem(values, setSubmitting);
    },
  });

  const saveItem = async (values: FormikTypes, setSubmitting: (isSubmitting: boolean) => void): Promise<void> => {
    setSubmitting(true);

    const newLocation = {
      column: parseInt(values.column, 10),
      letter: values.letter,
      level: parseInt(values.level, 10),
      chamberNumber: parseInt(values.chamberNumber, 10),
    };

    const newQuantities = {
      singleCuUnits: parseInt(values.singleCuUnits, 10),
      singleDpUnits: parseInt(values.singleDpUnits, 10),
      fullDuUnits: parseInt(values.fullDuUnits, 10),
    };

    const temporaryQuantity = quantityGetDtoToStockTransactionAddDto(initialData.item);

    const formStockTransactionDto: StockTransactionAddDto = {
      ...temporaryQuantity,
      ...newQuantities,
      ...newLocation,
      status: initialData.column,
      warehouseId: initialData.item.warehouse.id,
    };

    const addStockTransactionDto: StockTransactionAddDto = quantityGetDtoToStockTransactionAddDto(
      initialData.item,
    );

    const stockTransactionDto: ChangeStockTransactionDto = {
      formData: formStockTransactionDto,
      templateData: addStockTransactionDto,
    };

    const result = await InventoryApi.change(stockTransactionDto);

    if (result.status === StatusCodes.OK) {
      if (
        formStockTransactionDto.fullDuUnits === addStockTransactionDto.fullDuUnits &&
        formStockTransactionDto.singleDpUnits === addStockTransactionDto.singleDpUnits &&
        formStockTransactionDto.singleCuUnits === addStockTransactionDto.singleCuUnits
      ) {
        initialData.onYes(initialData.item, result.data.newQuantity);
      } else {
        initialData.onYes(initialData.item, result.data.leftoverQuantity, result.data.newQuantity);
      }
    }
  };

  Modal.setAppElement('#root');

  const { handleBlur, handleChange, values, touched, errors, setFieldValue, handleSubmit, isSubmitting } = formik;
  return (
    <Wrapper isOpen={initialData != null} style={{ overlay: { zIndex: 10 } }}>
      <form onSubmit={handleSubmit} autoComplete="off" style={{ padding: '10px' }}>
        <Row>
          <Col></Col>
          <Col>
            <StyleText>Full DU units</StyleText>
          </Col>
          <Col>
            <StyleText>Single DP units</StyleText>
          </Col>
          <Col>
            <StyleText>Single CU units</StyleText>
          </Col>
        </Row>
        <Row>
          <Col>
            <StyledTextTable>Avaliable:</StyledTextTable>
          </Col>
          <StyledCol style={{ borderTopLeftRadius: 10 }}>{initialData?.item?.fullDuUnits ?? 0}</StyledCol>
          <StyledCol>{initialData?.item?.singleDpUnits ?? 0}</StyledCol>
          <StyledCol style={{ borderTopRightRadius: 10 }}>{initialData?.item?.singleCuUnits ?? 0}</StyledCol>
        </Row>
        <Row>
          <Col>
            <StyledTextTable>Changing Status:</StyledTextTable>
          </Col>
          <StyledCol style={{ borderBottomLeftRadius: 10, height: '60px !important' }}>
            <TextInputComponent
              name="fullDuUnits"
              value={values.fullDuUnits}
              onBlur={handleBlur}
              onChange={handleChange}
              cols={14}
              errors={errors}
              touched={touched}
              height={25}
              style={{ margin: 0 }}
            />
          </StyledCol>
          <StyledCol style={{ height: '60px !important' }}>
            <TextInputComponent
              name="singleDpUnits"
              value={values.singleDpUnits}
              onBlur={handleBlur}
              onChange={handleChange}
              cols={14}
              errors={errors}
              touched={touched}
              style={{ margin: 0 }}
              height={25}
            />
          </StyledCol>
          <StyledCol style={{ borderBottomRightRadius: 10, height: '60px !important' }}>
            <TextInputComponent
              name="singleCuUnits"
              value={values.singleCuUnits}
              onBlur={handleBlur}
              onChange={handleChange}
              cols={14}
              errors={errors}
              touched={touched}
              style={{ margin: 0 }}
              height={25}
            />
          </StyledCol>
        </Row>
        <ActionWrapper>
          {/* <div> */}
          <LocationEditModal values={values} setField={setFieldValue} touched={touched} errors={errors} />
          {/* </div> */}
          <div>
            <PrimaryButton title="Submit" type="submit" isSubmitting={isSubmitting} />
          </div>
        </ActionWrapper>
        <WrapperCloseBtn>
          <BorderlessButton
            icon={xCircle}
            iconSize={25}
            onClick={(): void => {
              initialData?.onNo(null);
            }}
          />
        </WrapperCloseBtn>
      </form>
    </Wrapper>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  align-items: right;
  justify-content: space-between;
  margin-top: 43px;
`;

const Wrapper = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 300px;
  width: 700px;
  background: #fff;

  background: #f4f4f4;
  border-radius: 20px;
  padding: 35px;
  max-width: 760px;
  width: 100%;
`;

const WrapperCloseBtn = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`;

const StyledCol = styled(Col)`
  height: 39px;
  max-width: 100%;
  border: 1px solid #fff;
  vertical-align: middle;

  display: flex;
  align-items: center;
  div {
    width: 100%;
    max-width: 100%;
    margin: 0;
  }
`;

const StyleText = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #642b26;
`;

const StyledTextTable = styled.p`
  font-size: 14px;
  color: #a7a7a7;
  font-weight: 700;
  text-align: right;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: right;
`;
